import React from 'react';
import './TripSummarySidebar.css';

/**
 * TripSummarySidebar - Shows selected items and total cost
 * Sticky sidebar on right side of search page
 */
const TripSummarySidebar = ({ 
  selectedFlights = {},
  selectedHotel = null,
  selectedCar = null,
  onRemoveFlight,
  onRemoveHotel,
  onRemoveCar,
  onCheckout
}) => {

  // Calculate totals
  const outboundPrice = selectedFlights.outbound?.price || 0;
  const returnPrice = selectedFlights.return?.price || 0;
  const hotelPrice = selectedHotel?.totalPrice || 0;
  const carPrice = selectedCar?.totalPrice || 0;

  const flightTotal = outboundPrice + returnPrice;
  const totalCost = flightTotal + hotelPrice + carPrice;

  const hasItems = selectedFlights.outbound || selectedFlights.return || selectedHotel || selectedCar;

  const formatTime = (dateTime) => {
    if (!dateTime) return '';
    const date = new Date(dateTime);
    return date.toLocaleTimeString('en-US', { 
      hour: '2-digit', 
      minute: '2-digit',
      hour12: true 
    });
  };

  const formatDate = (dateTime) => {
    if (!dateTime) return '';
    const date = new Date(dateTime);
    return date.toLocaleDateString('en-US', { 
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <aside className="trip-summary-sidebar">
      <div className="trip-summary-card">
        {/* Header */}
        <div className="trip-summary-header">
          <div className="trip-summary-icon">🛒</div>
          <div>
            <div className="trip-summary-title">Your Trip</div>
            <div className="trip-summary-subtitle">
              {hasItems ? 'Review your selections' : 'No items selected'}
            </div>
          </div>
        </div>

        {/* Empty State */}
        {!hasItems && (
          <div className="trip-summary-empty">
            <div className="empty-icon">✈️</div>
            <p>Select flights, hotels, or rental cars to build your trip</p>
          </div>
        )}

        {/* Selected Items */}
        {hasItems && (
          <div className="trip-summary-items">
            
            {/* Outbound Flight */}
            {selectedFlights.outbound && (
              <div className="trip-item">
                <div className="trip-item-header">
                  <div className="trip-item-badge flight-badge">✈️ Outbound Flight</div>
                  <button 
                    className="trip-item-remove"
                    onClick={() => onRemoveFlight('outbound')}
                    title="Remove"
                  >
                    ×
                  </button>
                </div>
                <div className="trip-item-details">
                  <div className="trip-item-route">
                    <span className="trip-airport">{selectedFlights.outbound.origin}</span>
                    <span className="trip-arrow">→</span>
                    <span className="trip-airport">{selectedFlights.outbound.destination}</span>
                  </div>
                  <div className="trip-item-time">
                    {formatDate(selectedFlights.outbound.departureTime)} • {formatTime(selectedFlights.outbound.departureTime)}
                  </div>
                  <div className="trip-item-airline">
                    {selectedFlights.outbound.airline || selectedFlights.outbound.carrierCode} • 
                    {selectedFlights.outbound.stops === 0 ? ' Direct' : ` ${selectedFlights.outbound.stops} stop`}
                  </div>
                </div>
                <div className="trip-item-price">${selectedFlights.outbound.price}</div>
              </div>
            )}

            {/* Return Flight */}
            {selectedFlights.return && (
              <div className="trip-item">
                <div className="trip-item-header">
                  <div className="trip-item-badge flight-badge">✈️ Return Flight</div>
                  <button 
                    className="trip-item-remove"
                    onClick={() => onRemoveFlight('return')}
                    title="Remove"
                  >
                    ×
                  </button>
                </div>
                <div className="trip-item-details">
                  <div className="trip-item-route">
                    <span className="trip-airport">{selectedFlights.return.origin}</span>
                    <span className="trip-arrow">→</span>
                    <span className="trip-airport">{selectedFlights.return.destination}</span>
                  </div>
                  <div className="trip-item-time">
                    {formatDate(selectedFlights.return.departureTime)} • {formatTime(selectedFlights.return.departureTime)}
                  </div>
                  <div className="trip-item-airline">
                    {selectedFlights.return.airline || selectedFlights.return.carrierCode} • 
                    {selectedFlights.return.stops === 0 ? ' Direct' : ` ${selectedFlights.return.stops} stop`}
                  </div>
                </div>
                <div className="trip-item-price">${selectedFlights.return.price}</div>
              </div>
            )}

            {/* Hotel */}
            {selectedHotel && (
              <div className="trip-item">
                <div className="trip-item-header">
                  <div className="trip-item-badge hotel-badge">🏨 Hotel</div>
                  <button 
                    className="trip-item-remove"
                    onClick={onRemoveHotel}
                    title="Remove"
                  >
                    ×
                  </button>
                </div>
                <div className="trip-item-details">
                  <div className="trip-item-name">{selectedHotel.name}</div>
                  <div className="trip-item-time">
                    {selectedHotel.checkIn} → {selectedHotel.checkOut}
                  </div>
                  {selectedHotel.stars && (
                    <div className="trip-item-stars">
                      {'⭐'.repeat(selectedHotel.stars)}
                    </div>
                  )}
                </div>
                <div className="trip-item-price">${selectedHotel.totalPrice}</div>
              </div>
            )}

            {/* Car Rental */}
            {selectedCar && (
              <div className="trip-item">
                <div className="trip-item-header">
                  <div className="trip-item-badge car-badge">🚗 Rental Car</div>
                  <button 
                    className="trip-item-remove"
                    onClick={onRemoveCar}
                    title="Remove"
                  >
                    ×
                  </button>
                </div>
                <div className="trip-item-details">
                  <div className="trip-item-name">
                    {selectedCar.carCategory} - {selectedCar.carModel}
                  </div>
                  <div className="trip-item-time">
                    {selectedCar.pickupDate} → {selectedCar.returnDate}
                  </div>
                  <div className="trip-item-features">
                    {selectedCar.seats} seats • {selectedCar.automaticTransmission ? 'Automatic' : 'Manual'}
                  </div>
                </div>
                <div className="trip-item-price">${selectedCar.totalPrice}</div>
              </div>
            )}

            {/* Divider */}
            <div className="trip-summary-divider"></div>

            {/* Total */}
            <div className="trip-summary-total">
              <div className="total-row">
                <span className="total-label">Flights</span>
                <span className="total-amount">${flightTotal.toFixed(2)}</span>
              </div>
              {hotelPrice > 0 && (
                <div className="total-row">
                  <span className="total-label">Hotel</span>
                  <span className="total-amount">${hotelPrice.toFixed(2)}</span>
                </div>
              )}
              {carPrice > 0 && (
                <div className="total-row">
                  <span className="total-label">Car Rental</span>
                  <span className="total-amount">${carPrice.toFixed(2)}</span>
                </div>
              )}
              <div className="total-row grand-total">
                <span className="total-label">Total</span>
                <span className="total-amount">${totalCost.toFixed(2)}</span>
              </div>
            </div>

            {/* Checkout Button */}
            <button 
              className="checkout-button"
              onClick={onCheckout}
              disabled={!hasItems}
            >
              Continue to Checkout
            </button>
          </div>
        )}

        {/* Trust Message */}
        <div className="trip-summary-trust">
          <div className="trust-icon">🔒</div>
          <div className="trust-text">
            <strong>Secure Booking</strong>
            <span>Your payment info is encrypted and secure</span>
          </div>
        </div>
      </div>
    </aside>
  );
};

export default TripSummarySidebar;